import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import type { BreadcrumbItem, HeaderAction } from '@shared/components/navigation/types';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui';
import { useIsMobile } from '@shared/hooks/useMobile';
import type { RelocationDetail, RelocationStatus } from '../../models';

interface BreadcrumbProps {
  relocation?: RelocationDetail | null;
  /** Cuando es una reubicación nueva aún sin ID */
  isNew?: boolean;
  canDispatch?: boolean;
  isDispatching?: boolean;
  onDispatch?: () => void;
}

const statusBadgeClass = (status: RelocationStatus | undefined): string => {
  switch (status) {
    case 'draft':
      return 'text-badge-status-2 bg-badge-bg-status-2';
    case 'dispatched':
      return 'text-badge-status-1 bg-badge-bg-status-1';
    case 'cancelled':
      return 'text-badge-status-4 bg-badge-bg-status-4';
    default:
      return 'text-subtext bg-card-border';
  }
};

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  relocation,
  isNew,
  canDispatch = false,
  isDispatching = false,
  onDispatch,
}) => {
  const { t } = useTranslation('relocationProcessModule');
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const base: BreadcrumbItem[] = [];
    if (!isMobile) {
      base.push({ label: t('breadcrumb.relocations'), path: '/relocations' });
    }

    if (isNew || !relocation) {
      base.push({ label: t('breadcrumb.new') });
      return base;
    }

    const status = relocation.status;
    const statusLabel = t(`status.${status}`, { defaultValue: status });

    base.push({
      label: (
        <div className="flex items-center gap-3 text-title">
          <span>{t('breadcrumb.detail', { id: relocation.id })}</span>
          <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusBadgeClass(status)}`}>
            {statusLabel}
          </Badge>
        </div>
      ),
    });

    return base;
  }, [isMobile, isNew, relocation, t]);

  const headerActions = useMemo((): HeaderAction[] => {
    if (isMobile) {
      return [
        {
          id: `relocation-header-mobile-${canDispatch}-${isDispatching}`,
          label: '',
          onClick: () => {},
          component: (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={t('actions.header_menu_aria')}
                  data-testid="relocation-header-actions-dropdown-trigger"
                >
                  <MoreVertical className="h-4 w-4 text-icons" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem] p-1">
                <DropdownMenuItem
                  onSelect={() => navigate('/relocations')}
                  data-testid="header-action-back-to-list-mobile"
                >
                  {t('actions.back_to_list')}
                </DropdownMenuItem>
                {canDispatch && onDispatch ? (
                  <DropdownMenuItem
                    onSelect={() => onDispatch()}
                    disabled={isDispatching}
                    data-testid="header-action-dispatch-relocation-mobile"
                    className="mt-0.5 justify-start bg-primary text-btn-text font-medium hover:bg-primary/90 focus:bg-primary/90 focus:text-btn-text data-[highlighted]:bg-primary/90 data-[highlighted]:text-btn-text data-[disabled]:bg-primary/50"
                  >
                    {isDispatching ? t('actions.dispatching') : t('actions.dispatch')}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        },
      ];
    }

    const actions: HeaderAction[] = [
      {
        id: 'back-to-list',
        label: t('actions.back_to_list'),
        onClick: () => navigate('/relocations'),
        variant: 'outline',
      },
    ];

    if (canDispatch && onDispatch) {
      actions.push({
        id: 'dispatch-relocation',
        label: isDispatching ? t('actions.dispatching') : t('actions.dispatch'),
        onClick: onDispatch,
        variant: 'primary',
        disabled: isDispatching,
      });
    }

    return actions;
  }, [canDispatch, isDispatching, isMobile, navigate, onDispatch, t]);

  usePageNavigation({ breadcrumbs, headerActions });

  return null;
};
